import { useContext, useEffect, useMemo, useState } from 'react';
import { useWindowSize } from 'react-use';
import { cs } from '@bbkAdminUtils/css-utils';
import AddonsAccordion from '@bbkPricingPage/components/accordion/addons-accordion';
import RenderAlignedHtml from '@bbkPricingPage/components/card/components/aligned-html-render';
import { CTAButton } from '@bbkPricingPage/components/card/components/cta-button';
import { DiscountLabel } from '@bbkPricingPage/components/card/components/discount-label';
import { PlanRenewalText } from '@bbkPricingPage/components/card/components/plan-renew-text';
import { PriceBreakdown } from '@bbkPricingPage/components/card/components/price-breakdown';
import { PriceCardBadge } from '@bbkPricingPage/components/card/components/price-card-badge';
import { PriceCardHeader } from '@bbkPricingPage/components/card/components/price-card-header';
import { PriceComponent } from '@bbkPricingPage/components/card/components/price-component';
import { QuantitySlider } from '@bbkPricingPage/components/card/components/quantity-slider';
import { ShowTotalPrice } from '@bbkPricingPage/components/card/components/show-total-price';
import EqualHeightComponent from '@bbkPricingPage/components/equal-height-component/equal-height-component';
import { PricingTableContext } from '@bbkPricingPage/context/pricing-table-context';
import { type ShowPrice } from '@bbkPricingPage/events/defn';
import {
  calculateTotalPrice,
  calculateTotalPriceWithoutMonthly,
  getApplicableAddonsOrCharges,
} from '@bbkPricingPage/helpers/addon-helper';
import {
  getEffectiveAmount,
  getTotalEffectiveAmountWithoutMonthly,
} from '@bbkPricingPage/helpers/amount-calculator';
import { isCardsVertical } from '@bbkPricingPage/helpers/layout-helper';
import {
  getDefaultValueFromQuantityMeta,
  PricingTableHelper,
  resolvePlanQuantity,
  resolveQuantityMeta,
  type ActiveItemPrice,
} from '@bbkPricingPage/helpers/pricing-table-helper';
import {
  ItemType,
  type Discount,
  type Frequency,
  type Item,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';
import InfoText from './components/info-text';

export const PricingCard = ({
  item,
  price,
  frequency,
  noOfPlans,
  isFirst,
  isLast,
  pageHasBadge,
  applicableDiscounts = [],
  showPrice,
}: {
  item: Item;
  price: Price;
  frequency: Frequency;
  noOfPlans: number;
  isFirst: boolean;
  isLast: boolean;
  pageHasBadge: boolean;
  applicableDiscounts?: Discount[];
  showPrice?: ShowPrice;
}) => {
  const [isVerticalLayout, setIsVerticalLayout] = useState(false);
  const [ignoreEqualHeight, setIgnoreEqualHeight] = useState(false);
  const windowSize = useWindowSize();

  const {
    quantity: initQuantity,
    pricingTable,
    subscription,
    priceQuantities,
    setPriceQuantity,
    handlePrimaryCtaClick,
    isCheckoutLoading,
  } = useContext(PricingTableContext);

  const quantityMeta = useMemo(
    () => resolveQuantityMeta(item, price),
    [price.quantity_meta, price.pricing_model, item.customization.quantity_meta]
  );

  const subscriptionPlan = PricingTableHelper.getSubscribedPlan(subscription);
  const isSubscribedPrice =
    !!subscriptionPlan &&
    subscriptionPlan.billing_system_item_price_id === price.billing_system_id;

  const quantity = resolvePlanQuantity({
    priceId: price.billing_system_id,
    priceQuantities,
    initQuantity,
    isSubscribedPrice,
    subscribedPlanQuantity: subscriptionPlan?.quantity,
    quantityMeta,
  });

  const setQuantity = (newQuantity: number) => {
    setPriceQuantity(price.billing_system_id, newQuantity);
  };

  useEffect(() => {
    if (!quantityMeta) {
      return;
    }
    const priceId = price.billing_system_id;
    let clamped = quantity;
    if (quantityMeta.min && quantity <= quantityMeta.min) {
      clamped = quantityMeta.min;
    } else if (quantityMeta.max && quantity > quantityMeta.max) {
      clamped = quantityMeta.max;
    }
    if (clamped !== priceQuantities[priceId]) {
      setPriceQuantity(priceId, clamped);
    }
  }, [
    quantity,
    quantityMeta,
    price.billing_system_id,
    priceQuantities,
    setPriceQuantity,
  ]);

  useEffect(() => {
    const priceId = price.billing_system_id;
    if (isSubscribedPrice && subscriptionPlan?.quantity && quantityMeta) {
      setPriceQuantity(priceId, subscriptionPlan.quantity);
    } else if (initQuantity) {
      setPriceQuantity(priceId, initQuantity);
    } else if (quantityMeta?.default_value != null) {
      setPriceQuantity(priceId, getDefaultValueFromQuantityMeta(quantityMeta));
    }
  }, [
    quantityMeta,
    subscriptionPlan?.quantity,
    initQuantity,
    isSubscribedPrice,
    price.billing_system_id,
    setPriceQuantity,
  ]);

  const allAddons =
    pricingTable?.addons.filter(
      (aa) =>
        aa.item_type === ItemType.addon ||
        aa.item_type === undefined ||
        aa.item_type === null
    ) ?? [];

  const allCharges =
    pricingTable?.addons.filter((aa) => aa.item_type === ItemType.charge) ?? [];

  // Create selectedPlanPrice for getApplicableCharges
  const selectedPlanPrice = {
    item,
    price,
    isSubscribedItem: !!subscriptionPlan,
    isSubscribedPrice: !!isSubscribedPrice,
  } as ActiveItemPrice;

  const applicableCharges = getApplicableAddonsOrCharges({
    allAddons: allCharges,
    selectedPlanPrice,
    priceVariant: price.price_variant_id,
    subscription,
    isSubscribedPrice,
    itemType: ItemType.charge,
  });

  const applicableAddons = getApplicableAddonsOrCharges({
    allAddons,
    selectedPlanPrice,
    priceVariant: price.price_variant_id,
    subscription,
    isSubscribedPrice,
    itemType: ItemType.addon,
    frequency,
  });

  useEffect(() => {
    setIsVerticalLayout(isCardsVertical(windowSize.width, noOfPlans));
  }, [windowSize.width, noOfPlans]);

  useEffect(() => {
    setIgnoreEqualHeight(isCardsVertical(windowSize.width, noOfPlans));
  }, [windowSize.width, noOfPlans]);

  const mandatoryAddonsMap = useMemo(() => {
    const map: Record<string, number> = {};

    applicableAddons.forEach((addon) => {
      map[addon.addon.billing_system_id] = addon.quantity ?? 1;
    });
    applicableCharges.forEach((charge) => {
      map[charge.addon.billing_system_id] = charge.quantity ?? 1;
    });

    return map;
  }, [applicableAddons, applicableCharges]);

  const isBadgeAvailable = PricingTableHelper.hasBadge(item);

  if (!price) return null;
  const features = item.customization.features;

  const planPriceValue = getEffectiveAmount(
    price,
    quantity,
    frequency,
    applicableDiscounts
  );

  const mandatoryAddonsPriceValue = calculateTotalPrice({
    applicableAddons,
    frequency,
    itemType: ItemType.addon,
  });

  const chargesTotal = calculateTotalPrice({
    applicableAddons: applicableCharges,
    frequency,
    itemType: ItemType.charge,
  });

  // Find matching price config for ShowTotalPrice
  const matchingPriceConfig = showPrice?.configs?.find(
    (config) =>
      config.period === price.period && config.periodUnit === price.period_unit
  );
  // Calculate prices without monthly conversion for ShowTotalPrice
  const planTotalPriceValueWithoutMonthly =
    getTotalEffectiveAmountWithoutMonthly(price, quantity, applicableDiscounts);

  const mandatoryAddonsTotalPriceValueWithoutMonthly =
    calculateTotalPriceWithoutMonthly({
      applicableAddons,
      frequency,
      itemType: ItemType.addon,
    });

  const chargesTotalWithoutMonthly = calculateTotalPriceWithoutMonthly({
    applicableAddons: applicableCharges,
    frequency,
    itemType: ItemType.charge,
  });

  const showAddonsAndCharges =
    !!pricingTable?.customization?.show_addons_and_charges;
  const hidePriceBreakdown =
    !!pricingTable?.customization?.hide_price_breakdown;

  return (
    <div
      id={`item-${item.billing_system_id}`}
      className={cs(
        'tw-relative tw-w-full tw-flex-1 tw-border tw-border-solid tw-shadow-sm',
        'tw-z-10 tw-max-w-[600px] tw-border-[1px] tw-border-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_20%,transparent)] tw-transition-all',
        'tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
        'first:tw-rounded-bl-[calc(var(--pricingtable-border-radius)*1.2)] first:tw-rounded-tl-[calc(var(--pricingtable-border-radius)*1.2)]',
        'last:tw-rounded-br-[calc(var(--pricingtable-border-radius)*1.2)] last:tw-rounded-tr-[calc(var(--pricingtable-border-radius)*1.2)]',
        !isVerticalLayout && 'tw-max-w-[380px]',
        !item.customization.highlight &&
          noOfPlans && [
            noOfPlans == 1 &&
              'tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
            noOfPlans == 2 &&
              '@md:tw-max-w-[380px] @md:tw-rounded-none @lg:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
            noOfPlans == 3 &&
              '@md:tw-rounded-none @md:first:tw-border-r-0 @md:last:tw-border-l-0 @lg:tw-max-w-[380px] @lg:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)] @lg:first:tw-border-r @lg:last:tw-border-l',
            noOfPlans == 4 &&
              '@lg:tw-rounded-none @lg:tw-border-r-0 @lg:first:tw-border-r-0 @lg:last:tw-border-r @xl:tw-max-w-[380px] @xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)] @xl:tw-border-r @xl:first:tw-border-r',
            noOfPlans == 5 &&
              '@xl:tw-rounded-none @xl:tw-border-r-0 @xl:first:tw-border-r-0 @xl:last:tw-border-r @2xl:tw-max-w-[380px] @2xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)] @2xl:tw-border-r @2xl:first:tw-border-r',
            noOfPlans >= 6 && 'tw-flex-col tw-items-stretch',
          ],
        item.customization.highlight &&
          !isVerticalLayout &&
          noOfPlans &&
          '-tw-my-2',

        item.customization.highlight &&
          cs(
            'tw-z-10 tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)] tw-border-2 tw-border-[var(--pricingtable-badge-color)] tw-shadow-xl',
            'first:tw-rounded-bl-[calc(var(--pricingtable-border-radius)*1.2)] first:tw-rounded-tl-[calc(var(--pricingtable-border-radius)*1.2)]',
            'last:tw-rounded-br-[calc(var(--pricingtable-border-radius)*1.2)] last:tw-rounded-tr-[calc(var(--pricingtable-border-radius)*1.2)]',
            noOfPlans > 1 &&
              noOfPlans <= 3 &&
              '@md:tw-rounded-[min(var(--pricingtable-border-radius),12px)] @md:first:tw-rounded-bl-[min(var(--pricingtable-border-radius),12px)] @md:first:tw-rounded-tl-[min(var(--pricingtable-border-radius),12px)] @md:last:tw-rounded-br-[min(var(--pricingtable-border-radius),12px)] @md:last:tw-rounded-tr-[min(var(--pricingtable-border-radius),12px)] @lg:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)] @lg:first:tw-rounded-bl-[calc(var(--pricingtable-border-radius)*1.2)] @lg:first:tw-rounded-tl-[calc(var(--pricingtable-border-radius)*1.2)] @lg:last:tw-rounded-br-[calc(var(--pricingtable-border-radius)*1.2)] @lg:last:tw-rounded-tr-[calc(var(--pricingtable-border-radius)*1.2)]',
            noOfPlans === 4 &&
              '@lg:tw-rounded-[min(var(--pricingtable-border-radius),12px)] @lg:first:tw-rounded-bl-[min(var(--pricingtable-border-radius),12px)] @lg:first:tw-rounded-tl-[min(var(--pricingtable-border-radius),12px)] @lg:last:tw-rounded-br-[min(var(--pricingtable-border-radius),12px)] @lg:last:tw-rounded-tr-[min(var(--pricingtable-border-radius),12px)] @xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)] @xl:first:tw-rounded-bl-[calc(var(--pricingtable-border-radius)*1.2)] @xl:first:tw-rounded-tl-[calc(var(--pricingtable-border-radius)*1.2)] @xl:last:tw-rounded-br-[calc(var(--pricingtable-border-radius)*1.2)] @xl:last:tw-rounded-tr-[calc(var(--pricingtable-border-radius)*1.2)]',
            noOfPlans === 5 &&
              '@xl:tw-rounded-[min(var(--pricingtable-border-radius),12px)] @xl:first:tw-rounded-bl-[min(var(--pricingtable-border-radius),12px)] @xl:first:tw-rounded-tl-[min(var(--pricingtable-border-radius),12px)] @xl:last:tw-rounded-br-[min(var(--pricingtable-border-radius),12px)] @xl:last:tw-rounded-tr-[min(var(--pricingtable-border-radius),12px)] @2xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)] @2xl:first:tw-rounded-bl-[calc(var(--pricingtable-border-radius)*1.2)] @2xl:first:tw-rounded-tl-[calc(var(--pricingtable-border-radius)*1.2)] @2xl:last:tw-rounded-br-[calc(var(--pricingtable-border-radius)*1.2)] @2xl:last:tw-rounded-tr-[calc(var(--pricingtable-border-radius)*1.2)]'
          )
      )}
    >
      <div
        style={{
          backgroundColor: 'var(--pricing-page-card-background-color)',
        }}
        className={cs(
          // Tooltip bubble (.slate-tooltip::after) is absolutely positioned and gets clipped
          // by overflow-hidden wrappers; keep card content overflow visible for tooltip UX.
          'tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-visible',
          'tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
          item.customization.highlight
            ? cs(
                'tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)] tw-pt-[7px]',
                noOfPlans > 1 &&
                  noOfPlans <= 3 &&
                  '@md:tw-rounded-[min(var(--pricingtable-border-radius),12px)] @lg:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
                noOfPlans === 4 &&
                  '@lg:tw-rounded-[min(var(--pricingtable-border-radius),12px)] @xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
                noOfPlans === 5 &&
                  '@xl:tw-rounded-[min(var(--pricingtable-border-radius),12px)] @2xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]'
              )
            : // Note: the padding pt should be corresponding to negative margin my
              //  eg: my-2 => (pt-2 - 1px) => pt-[7px]
              cs(
                noOfPlans <= 3 &&
                  !isFirst &&
                  !isLast &&
                  '@md:tw-rounded-none @lg:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
                noOfPlans == 4 &&
                  !isFirst &&
                  !isLast &&
                  '@lg:tw-rounded-none @xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',

                noOfPlans == 5 &&
                  !isFirst &&
                  !isLast &&
                  '@xl:tw-rounded-none @2xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',

                noOfPlans <= 3 &&
                  isFirst &&
                  !isLast && // don't add these classes when there's only one plan card
                  '@md:tw-rounded-br-none @md:tw-rounded-tr-none @lg:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
                noOfPlans <= 3 &&
                  isLast &&
                  !isFirst && // don't add these classes when there's only one plan card
                  '@md:tw-rounded-bl-none @md:tw-rounded-tl-none @lg:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',

                noOfPlans == 4 &&
                  isFirst &&
                  '@lg:tw-rounded-br-none @lg:tw-rounded-tr-none @xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
                noOfPlans == 4 &&
                  isLast &&
                  '@lg:tw-rounded-bl-none @lg:tw-rounded-tl-none @xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',

                noOfPlans == 5 &&
                  isFirst &&
                  '@xl:tw-rounded-br-none @xl:tw-rounded-tr-none @2xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
                noOfPlans == 5 &&
                  isLast &&
                  '@xl:tw-rounded-bl-none @xl:tw-rounded-tl-none @2xl:tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',

                noOfPlans >= 6 &&
                  !isFirst &&
                  !isLast &&
                  'tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
                noOfPlans >= 6 &&
                  isFirst &&
                  'tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]',
                noOfPlans >= 6 &&
                  isLast &&
                  'tw-rounded-[calc(var(--pricingtable-border-radius)*1.2)]'
              )
        )}
      >
        {/* ************ Plan Main Info ********************** */}
        <div className="tw-overflow-hidden tw-rounded-t-[inherit]">
          <PriceCardBadge
            id={item.billing_system_id}
            badge={item.customization.badge}
          />
          <div
            className={cs(
              'tw-flex tw-flex-col tw-items-center tw-px-6 tw-pb-6',
              // i want to modify the line below to conditionally add a class when item.highlight is present to any of the pricing cards
              'tw-mt-2 tw-pt-6',
              pageHasBadge && !isVerticalLayout && 'tw-mt-8',
              isBadgeAvailable && isVerticalLayout && 'tw-mt-8'
            )}
          >
            <div className="tw-w-full">
              <PriceCardHeader
                id={item.billing_system_id}
                name={item.customization.name}
                displayName={item.customization.display_name}
                description={item.customization.description}
                ignoreEqualHeight={ignoreEqualHeight}
              />
              <div className="tw-mt-4 tw-flex tw-justify-center tw-pb-2">
                <DiscountLabel
                  label={applicableDiscounts?.[0]?.label}
                  show={
                    pricingTable?.customization?.frequency_orientation === 'row'
                  }
                  ignoreEqualHeight={ignoreEqualHeight}
                />
              </div>
              <PriceComponent
                id={price.billing_system_id}
                frequency={frequency as Frequency}
                price={price}
                item={item}
                quantity={quantity as number}
                applicableDiscounts={applicableDiscounts}
                frequencyOrientation={
                  pricingTable?.customization?.frequency_orientation || 'col'
                }
                mandatoryAddonsPriceValue={
                  showAddonsAndCharges
                    ? mandatoryAddonsPriceValue + chargesTotal
                    : undefined
                }
                ignoreEqualHeight={ignoreEqualHeight}
              />

              <PlanRenewalText
                showPriceRenewal={
                  showAddonsAndCharges && !!applicableCharges.length
                }
                text={frequency.recurring_price_label}
                price={planPriceValue + mandatoryAddonsPriceValue}
                currency={price.currency}
                ignoreEqualHeight={ignoreEqualHeight}
              />

              <PriceBreakdown
                showPriceBreakdown={
                  !hidePriceBreakdown &&
                  !item.customization.is_custom_amount_text &&
                  showAddonsAndCharges
                }
                planPriceValue={planPriceValue}
                mandatoryAddonsPriceValue={mandatoryAddonsPriceValue}
                chargeValue={chargesTotal}
                currency={price.currency}
                ignoreEqualHeight={ignoreEqualHeight}
              />

              <QuantitySlider
                id={item.billing_system_id}
                quantity={quantity as number}
                quantityMeta={quantityMeta}
                onValueChange={setQuantity}
                ignoreEqualHeight={ignoreEqualHeight}
              />

              {matchingPriceConfig && frequency.show_monthly_price && (
                <EqualHeightComponent
                  groupId="dynamic-height-pricing-card-show-total-price"
                  ignore={ignoreEqualHeight}
                >
                  <ShowTotalPrice
                    priceConfig={matchingPriceConfig}
                    priceValue={
                      planTotalPriceValueWithoutMonthly +
                      (showAddonsAndCharges
                        ? mandatoryAddonsTotalPriceValueWithoutMonthly +
                          chargesTotalWithoutMonthly
                        : 0)
                    }
                    currency={price.currency}
                  />
                </EqualHeightComponent>
              )}
            </div>
            <CTAButton
              itemId={item.billing_system_id}
              price={price}
              actionButton={item.customization.action_button}
              paywallConfig={pricingTable?.paywall_config}
              isSubscribedPrice={isSubscribedPrice}
              quantity={quantity}
              quantityMeta={quantityMeta}
              subscriptionQuantity={subscriptionPlan?.quantity ?? 1}
              subscriptionStatus={subscription?.status}
              hasChanges={false}
              disabled={isCheckoutLoading}
              onClick={() => {
                handlePrimaryCtaClick({
                  item,
                  price,
                  quantity: quantity ?? 1,
                  subscription,
                  isSubscribedPrice,
                  paywallConfig: pricingTable?.paywall_config,
                  mandatoryAddonsMap,
                });
              }}
            />

            <InfoText
              ignoreEqualHeight={ignoreEqualHeight}
              infoText={item.customization.info_text ?? ''}
            />
          </div>
        </div>

        {/* ************ Plan Entitlements ********************** */}

        <EqualHeightComponent
          groupId="dynamic-height-pricing-card-features"
          ignore={ignoreEqualHeight}
        >
          <div
            id={`item-features-${item.billing_system_id}`}
            className={cs(
              'markdown-wrapper tw-px-12 tw-pb-2 tw-pl-12 tw-pr-8 tw-text-[var(--pricing-page-text-font-text-color)] @sm:tw-px-24',
              noOfPlans && [
                noOfPlans <= 3 && '@md:tw-px-12',
                noOfPlans <= 4 && '@lg:tw-px-12',
                noOfPlans <= 5 && '@xl:tw-px-12',
              ]
            )}
          >
            <RenderAlignedHtml
              value={features ?? ''}
              className="tw-text-[16px]"
            />
          </div>
        </EqualHeightComponent>

        <div className="tw-pb-2">
          <AddonsAccordion
            hideMandatoryAddonPrices={
              !!pricingTable?.customization?.hide_mandatory_addon_prices
            }
            priceFrequencies={pricingTable?.frequencies ?? []}
            addonSupportEnabled={
              !!pricingTable?.customization?.show_addons_and_charges
            }
            applicableAddons={applicableAddons}
            applicableCharges={applicableCharges}
            itemFrequency={frequency}
            item={item}
          />
        </div>
      </div>
    </div>
  );
};
